/**
 * Live registration funnel for a project, from its participant documents:
 * started (entered an ID or email) -> pressed Connect -> connected, plus who is connected now.
 * "Pressed Connect, did not finish" is mostly people who stopped at Google's unverified-app
 * warning or permissions screen.
 */
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { db } from '../../firebase';
import participantStage from '../utils/participantStage';

const pct = (n, d) => (d ? `${Math.round((n / d) * 100)}%` : '–');

function Bar({ label, n, of, note }) {
  const w = of == null ? (n ? 100 : 0) : of ? Math.max(2, Math.round((n / of) * 100)) : 0;
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 4 }}>
        <span>{label}</span>
        <span style={{ fontFamily: 'var(--jp-mono)', fontSize: 12 }}>{n}{of != null ? ` · ${pct(n, of)}` : ''}</span>
      </div>
      <div style={{ height: 8, background: 'var(--jp-gray-100)', borderRadius: 4, overflow: 'hidden' }}>
        <div style={{ width: `${w}%`, height: '100%', background: 'var(--jp-blue-mid)' }} />
      </div>
      {note && <div style={{ fontSize: 12, color: 'var(--jp-gray-600)', marginTop: 4 }}>{note}</div>}
    </div>
  );
}
Bar.propTypes = { label: PropTypes.string, n: PropTypes.number, of: PropTypes.number, note: PropTypes.node };

export default function EnrollmentFunnel({ projectId }) {
  const [counts, setCounts] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => onSnapshot(
    query(collection(db, 'participants'), where('projectId', '==', projectId)),
    (snap) => {
      const c = { registered: 0, authorizing: 0, connected: 0, disconnected: 0, total: snap.size };
      snap.docs.forEach((d) => { c[participantStage(d.data())] += 1; });
      setCounts(c);
      setError(null);
    },
    (err) => setError(err.message),
  ), [projectId]);

  if (error) return <p style={{ fontSize: 13, color: '#B42318' }}>Could not load participants: {error}</p>;
  if (!counts) return <p style={{ fontSize: 13, color: 'var(--jp-gray-600)' }}>Loading…</p>;
  const pressed = counts.authorizing + counts.connected + counts.disconnected;
  const everConnected = counts.connected + counts.disconnected;
  return (
    <div>
      <Bar label="Started registration" n={counts.total} of={null} />
      <Bar label="Pressed Connect Google Health" n={pressed} of={counts.total}
        note={counts.registered ? `${counts.registered} entered an ID but never pressed Connect.` : null} />
      <Bar label="Connected" n={everConnected} of={counts.total}
        note={counts.authorizing
          ? `${counts.authorizing} pressed Connect but did not finish: mostly people who stopped at Google's unverified-app warning or permissions screen.`
          : null} />
      <Bar label="Connected now" n={counts.connected} of={counts.total}
        note={counts.disconnected ? `${counts.disconnected} connected, then disconnected or revoked access.` : null} />
      <p style={{ fontSize: 12, color: 'var(--jp-gray-600)', margin: '4px 0 0' }}>Updates live as people register.</p>
    </div>
  );
}

EnrollmentFunnel.propTypes = { projectId: PropTypes.string.isRequired };
